<template>
  <div
    class="product-inline-search"
    :class="{ 'product-inline-search--has-results': products.length > 0 }"
  >
    <div class="product-inline-search__search-container">
      <AkInputDropdown
        :options="productOptions"
        :show-chevron="true"
        :label="t('account.brands.createCampaigns.editor.shared.productInlineSearch.searchBar.label')"
        :placeholder="t('account.brands.createCampaigns.editor.shared.productInlineSearch.searchBar.placeholder')"
        dropdown-max-height="400px"
        icon-left="search"
        @update:model-value="onSearch($event)"
        @select-option="onSelect($event)"
        @click.stop
      >
        <template #optionItem="{ option }">
          <div class="product-inline-search__option-item">
            <img
              :src="imageCdnService.createUrl(option.images?.[0], '25')"
              :alt="option.name"
              class="product-inline-search__option-item-img"
            >
            <div>
              <div class="product-inline-search__option-item-name">
                {{ option.name }}
              </div>
              <div class="product-inline-search__option-item-price">
                {{ formatPrice(option.wholesale_price) }}
              </div>
            </div>
          </div>
        </template>
      </AkInputDropdown>
      <LoaderIcon
        v-if="isLoading"
        class="product-inline-search__loader"
        small
      />
    </div>
    <div
      v-if="selectedProducts.length > 0"
      class="product-inline-search__results-container"
    >
      <div class="product-inline-search__results-header">
        <AkParagraph class="product-inline-search__results-title">
          {{ t('account.brands.createCampaigns.editor.shared.productInlineSearch.results.title', { count: selectedProducts.length }) }}
        </AkParagraph>
        <div class="product-inline-search__results-header-toggle">
          <AkParagraph class="product-inline-search__prices-label">
            {{ t('account.brands.createCampaigns.editor.shared.productInlineSearch.results.pricesLabel') }}
          </AkParagraph>
          <AkToggleV2
            v-model:value="shouldShowPrices"
            data-testid="shouldShowPrices-toggle"
          />
        </div>
      </div>
      <VueDraggable
        v-if="selectedProducts.length > 0"
        class="product-inline-search__results-list"
        :model-value="selectedProducts"
        item-key="id"
        :invert-swap="false"
        :animation="150"
        :delay="200"
        :delay-on-touch-only="true"
        easing="cubic-bezier(1, 0, 0, 1)"
        ghost-class="ds-invisible"
        data-testid="vue-draggable"
        @update:model-value="onChangeOrder"
      >
        <template #item="{ element: gridItem, index }">
          <ProductCard
            :key="gridItem.id"
            class="product-inline-search__results-item"
            :name="gridItem.name"
            :img-src="gridItem.imgSrc"
            :price="gridItem.price"
            :should-show-prices="shouldShowPrices"
            :image-cdn-service="imageCdnService"
            data-testid="product-card"
            @remove-product="removeProduct(index)"
          />
        </template>
      </VueDraggable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AkParagraph, AkInputDropdown, AkToggleV2 } from '@ankorstore/design-system';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@core/utilities/lodash-utils';
import { getProducts } from '@monolith/legacy/services/api/brand-me';
import { Product } from '@monolith/legacy/types/api/product';
import { ProductCard } from '../../product-card';
import VueDraggable from 'vuedraggable';
import usePrice from '@client/monolith/core/composables/use-price';
import { computed } from 'vue';
import LoaderIcon from '@client/monolith/legacy/components/loader-icon.vue';
import { Amount } from '@client/monolith/core/types/amount';
import { ImageCdnService } from '../../../types';

type SimpleProduct = {
  uuid: string;
  name: string;
  imgSrc?: string;
  price: Amount;
  link: string;
};

const { t } = useI18n();

const { formatPrice } = usePrice();

defineOptions({
  name: 'ProductInlineSearch'
});

defineProps<{
  imageCdnService: ImageCdnService;
}>();

const toSimpleProduct = (product: { uuid: string; name: string; images?: string[]; wholesale_price: Amount; link: string }): SimpleProduct => {
  return {
    uuid: product.uuid,
    name: product.name,
    imgSrc: product.images?.[0],
    price: product.wholesale_price,
    link: product.link,
  };
};

const products = ref<Product[]>([]);
const isLoading = ref(false);

const shouldShowPrices = defineModel<boolean>('shouldShowPrices', { default: true });
const selectedProducts = defineModel<SimpleProduct[]>('selectedProducts', { default: [] });

const onChangeOrder = (products: SimpleProduct[]) => {
  selectedProducts.value = products;
};

const onSelect = (option: Product) => {
  selectedProducts.value = [...selectedProducts.value, toSimpleProduct(option)];
};

const removeProduct = (index: number) => {
  selectedProducts.value = selectedProducts.value.filter((_, i) => i !== index);
};

const onSearch = debounce(async (query: string): Promise<void> => {
  if (!query || query.length < 3) {
    products.value = [];
    return;
  }
  isLoading.value = true;

  const data = await getProducts({
    params: {
      page: 1,
      perPage: 20,
      query,
      'filters[requireupdate]': 0,
      'filters[with_options]': 0,
    },
  });

  products.value = data.data;
  isLoading.value = false;
}, 300);

const productOptions = computed(() => {
  return products.value.map(product => ({
    ...product,
    disabled: selectedProducts.value.some(selectedProduct => selectedProduct.uuid === product.uuid),
  }));
});
</script>


<style scoped lang="scss">
.product-inline-search {
    &__search-container {
        @apply ds-bg-neutral-100 ds-p-4 ds-rounded-md ds-relative;
    }

    &__results-container {
        @apply ds-bg-info-100 ds-p-4 ds-rounded-md;
    }

    &__results-title {
        @apply ds-text-neutral-900 ds-text-lg ds-font-bold ds-mb-5;
    }

    &__results-header {
        @apply ds-flex ds-justify-between ds-items-center;
    }

    &__results-list {
        @apply ds-flex ds-flex-wrap ds-gap-4 ds-mt-4;
    }

    &__results-item {
      @apply ds-cursor-move;
    }

    &__results-header-toggle {
      @apply ds-flex ds-items-center ds-gap-2;
    }

    &__prices-label {
      @apply ds-text-neutral-900 ds-text-sm;
    }

    &__option-item {
      @apply ds-text-sm ds-flex ds-items-center ds-gap-2 ds-p-2;
    }

    &__option-item-img {
      @apply ds-rounded-sm;
    }

    &__option-item-price {
      @apply ds-font-bold;
    }

    &__loader {
      @apply ds-absolute ds-bottom-5.5 ds-right-8;
    }
}

.product-inline-search:not(.product-inline-search--has-results) {
  :deep(.ak-dropdown--open) {
    display: none
  }
}


</style>
